import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import {
  AttachmentCountButton,
  Attachments,
  useAttachmentCounts,
} from '../../components/Attachments';
import { useToast } from '../../components/Toast';
import { RecordCollectionModal } from './RecordCollectionModal';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportNum,
  exportPeriod,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

interface PaymentRow {
  id: string;
  number: string;
  amount: string;
  method: string | null;
  reference: string | null;
  paidAt: string;
  account: { id: string; name: string } | null;
  invoice: { id: string; number: string } | null;
}

interface PaymentPage {
  items: PaymentRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** أعمدة الجدول بمفاتيحها — المفتاح نفسه مفتاح الفلتر في رأس العمود. */
const PAYMENT_COLUMNS = [
  { key: 'number', label: 'الرقم' },
  { key: 'account', label: 'الحساب' },
  { key: 'invoice', label: 'الفاتورة' },
  { key: 'amount', label: 'المبلغ' },
  { key: 'method', label: 'الطريقة' },
  { key: 'reference', label: 'المرجع' },
  { key: 'paidAt', label: 'التاريخ' },
] as const;

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في payments.service.ts. */
const PAYMENT_FILTERS: FilterDef[] = [
  { key: 'number', label: 'الرقم', kind: 'text', sortable: true },
  { key: 'account', label: 'الحساب', kind: 'text' },
  { key: 'invoice', label: 'الفاتورة', kind: 'text' },
  { key: 'amount', label: 'المبلغ', kind: 'number', sortable: true },
  { key: 'method', label: 'الطريقة', kind: 'text' },
  { key: 'reference', label: 'المرجع', kind: 'text' },
  { key: 'paidAt', label: 'التاريخ', kind: 'date', sortable: true },
];

export function PaymentsList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [reversing, setReversing] = useState<PaymentRow | null>(null);
  const [recording, setRecording] = useState(false);
  const [filesFor, setFilesFor] = useState<PaymentRow | null>(null);
  const toast = useToast();

  /* العكس والتسجيل من هنا والسندات كلها للمدير فما فوق — كمرفقات «التحصيل». */
  const canReverse = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['payments', query],
    queryFn: () => api<PaymentPage>(`/finance/payments${qs(query)}`),
  });

  /* عدد السندات لصفوف الصفحة كلها في طلب واحد. */
  const fileCounts = useAttachmentCounts(
    'payment',
    canReverse ? (list.data?.items ?? []).map((p) => p.id) : [],
  );

  const reverse = useMutation({
    mutationFn: (id: string) => api(`/finance/payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setReversing(null);
    },
  });

  const pageSum = (list.data?.items ?? []).reduce((s, p) => s + Number(p.amount), 0);

  /* التصدير: كل التحصيلات المطابقة للبحث والمدى والفلاتر، لا الصفحة المعروضة وحدها. */
  const buildExport = async (): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<PaymentRow>('/finance/payments', query, 100);
    return {
      title: 'التحصيلات',
      sheets: [
        {
          title: 'التحصيلات',
          period: exportPeriod(state.from, state.to),
          sections: [
            {
              columns: [
                { key: 'number', label: 'الرقم', kind: 'code' },
                { key: 'account', label: 'الحساب', minWidth: 16 },
                { key: 'invoice', label: 'الفاتورة', wrap: false },
                { key: 'amount', label: 'المبلغ', kind: 'money', total: true },
                { key: 'method', label: 'الطريقة' },
                { key: 'reference', label: 'المرجع' },
                { key: 'paidAt', label: 'التاريخ', kind: 'date' },
              ],
              rows: items.map((p) => ({
                number: p.number,
                account: p.account?.name ?? null,
                invoice: p.invoice?.number ?? 'تحت الحساب',
                amount: exportNum(p.amount),
                method: p.method,
                reference: p.reference,
                paidAt: exportDay(p.paidAt),
              })),
            },
          ],
          notes: exportNotes({
            q: debounced,
            conditions: describeFilters(PAYMENT_FILTERS, filters),
            count: items.length,
            total,
            truncated,
          }),
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="التحصيلات"
        subtitle={list.data ? `${list.data.total} عملية تحصيل` : undefined}
        actions={
          canReverse && (
            <button className="btn btn-primary" onClick={() => setRecording(true)}>
              <Icon name="plus" size={15} />
              تسجيل تحصيل
            </button>
          )
        }
      />

      <div className="stats">
        <Stat label="محصّل في هذه الصفحة" value={<span className="num">{money(pageSum)}</span>} />
      </div>

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالرقم أو المرجع"
        />

        {/* مدى تاريخ التحصيل — وبطاقة «محصّل في هذه الصفحة» تتبعه لأنها من الصفحة المعروضة. */}
        <DateRangeFilter
          from={state.from}
          to={state.to}
          onChange={(f, t) => patch({ from: f, to: t })}
        />

        <ExportMenu build={buildExport} disabled={!list.data?.total} />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={PAYMENT_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={PAYMENT_FILTERS} filters={filters} onChange={setFilters} />

      {/*
        تحصيلات فريق المبيعات — ما سُجّل من ملفات العملاء يقف هنا
        «بانتظار الاعتماد» حتى يتأكد المحاسب أن المال وصل فعلًا،
        وإيصاله المرفق يُفتح للمراجعة قبل الاعتماد.
      */}
      <SalesCollections canConfirm={canReverse} />

      <FilterScope
        defs={PAYMENT_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/finance/payments/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {PAYMENT_COLUMNS.map((c) => (
                  <th key={c.key}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                {canReverse && <th />}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={canReverse ? 8 : 7} />}

            {list.data && (
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-strong num">{p.number}</td>
                    <td>
                      {p.account ? (
                        <Link to={`/crm/accounts/${p.account.id}`}>{p.account.name}</Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {p.invoice ? (
                        <Link to={`/finance/invoices/${p.invoice.id}`} className="num">
                          {p.invoice.number}
                        </Link>
                      ) : (
                        <span className="cell-mute">تحت الحساب</span>
                      )}
                    </td>
                    <td className="num strong">{money(p.amount)}</td>
                    <td className="cell-mute">{p.method ?? '—'}</td>
                    <td className="cell-mute">
                      <bdi>{p.reference ?? '—'}</bdi>
                    </td>
                    <td className="cell-mute date">{date(p.paidAt)}</td>
                    {canReverse && (
                      <td className="end nowrap">
                        <AttachmentCountButton
                          count={fileCounts[p.id] ?? 0}
                          onClick={() => setFilesFor(p)}
                          label={`سندات التحصيل ${p.number}`}
                        />
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setReversing(p)}
                          aria-label={`عكس التحصيل ${p.number}`}
                          title="عكس التحصيل"
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد تحصيلات"
            text="سجّل التحصيل من زرّ «تسجيل تحصيل» أعلاه — على فاتورة مفتوحة أو تحت الحساب."
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
            onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
          />
        )}
      </div>
      </FilterScope>

      {recording && (
        <RecordCollectionModal
          onClose={() => setRecording(false)}
          onDone={(number) => {
            setRecording(false);
            // الدفعة تغيّر الفواتير والأرصدة والقيود معًا — لا القائمة وحدها.
            void queryClient.invalidateQueries();
            toast(`سُجّل التحصيل ${number}`);
          }}
        />
      )}

      {/* سندات الدفعة: عرض ورفع وحذف. */}
      {filesFor && (
        <Modal title={`سندات التحصيل ${filesFor.number}`} onClose={() => setFilesFor(null)}>
          <Attachments entity="payment" entityId={filesFor.id} title="سند التحصيل" />
        </Modal>
      )}

      {reversing && (
        <Modal
          title={`عكس التحصيل ${reversing.number}`}
          onClose={() => setReversing(null)}
          footer={
            <>
              <button className="btn" onClick={() => setReversing(null)} type="button">
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                onClick={() => reverse.mutate(reversing.id)}
                disabled={reverse.isPending}
              >
                {reverse.isPending ? 'جارٍ العكس' : 'تأكيد العكس'}
              </button>
            </>
          }
        >
          {reverse.isError && (
            <div className="alert alert-danger mb-4">
              {reverse.error instanceof Error ? reverse.error.message : 'تعذّر العكس.'}
            </div>
          )}
          <p>
            سيُحذف تحصيل بمبلغ <span className="num strong">{money(reversing.amount)}</span>
            {reversing.invoice && (
              <>
                {' '}
                من الفاتورة <span className="num">{reversing.invoice.number}</span>
              </>
            )}
            .
          </p>
          <p className="small mute mt-2">
            تُعاد حالة الفاتورة ورصيدها تلقائيًا بعد العكس. استخدم هذا لتصحيح إدخال خاطئ فقط.
          </p>
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   تحصيلات فريق المبيعات — تُعتمد بعد التأكد من وصول المال
   ========================================================================== */

interface SalesPaymentRow extends PaymentRow {
  reviewStatus: 'PENDING' | 'CONFIRMED';
  cashAccount?: { name: string } | null;
}

/**
 * ما سجّله المندوب من ملف العميل دفعةٌ حقيقية في هذه الدفاتر — لكنه
 * ادّعاء تحصيلٍ حتى يُعتمد: المحاسب يفتح الإيصال المرفق ويتأكد أن
 * المال وصل، ثم يعتمد. المعلَّق أولًا، والمعتمَد يُطوى خلفه.
 */
function SalesCollections({ canConfirm }: { canConfirm: boolean }) {
  const queryClient = useQueryClient();
  const [receiptFor, setReceiptFor] = useState<SalesPaymentRow | null>(null);

  const list = useQuery({
    queryKey: ['payments', 'sales-collections'],
    queryFn: () =>
      api<{ items: SalesPaymentRow[] }>(
        `/finance/payments${qs({ recordedVia: 'SALES', pageSize: 50, sort: 'paidAt', order: 'desc' })}`,
      ),
  });

  const confirm = useMutation({
    mutationFn: (id: string) => api(`/finance/payments/${id}/confirm`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['payments'] }),
  });

  const items = [...(list.data?.items ?? [])].sort((a, b) =>
    a.reviewStatus === b.reviewStatus ? 0 : a.reviewStatus === 'PENDING' ? -1 : 1,
  );
  const pending = items.filter((p) => p.reviewStatus === 'PENDING').length;

  if (items.length === 0) return null;

  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">تحصيلات فريق المبيعات</h2>
        {pending > 0 ? (
          <span className="badge badge-warning">{pending} بانتظار الاعتماد</span>
        ) : (
          <span className="tiny mute">كلها معتمدة</span>
        )}
      </div>

      {/*
        حتى خمسين صفًّا فوق الجدول الرئيسي — سقفٌ بعدد الصفوف ورأسٌ يلتصق،
        وإلا دفع الجدولَ الرئيسي إلى أسفل الصفحة.
      */}
      <div className="table-wrap sticky-head rows-cap" style={{ ['--rows' as string]: 6 }}>
        <table className="table">
          <thead>
            <tr>
              <th>الرقم</th>
              <th>الحساب</th>
              <th>الفاتورة</th>
              <th>المبلغ</th>
              <th>المصدر</th>
              <th>التاريخ</th>
              <th>الحالة</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} style={p.reviewStatus === 'CONFIRMED' ? { opacity: 0.7 } : undefined}>
                <td className="num cell-strong">{p.number}</td>
                <td>
                  {p.account ? (
                    <Link to={`/crm/accounts/${p.account.id}`} className="link">
                      {p.account.name}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="num">
                  {p.invoice ? (
                    <Link to={`/finance/invoices/${p.invoice.id}`} className="link num">
                      {p.invoice.number}
                    </Link>
                  ) : (
                    'تحت الحساب'
                  )}
                </td>
                <td className="num">{money(p.amount)}</td>
                <td className="cell-mute">{p.cashAccount?.name ?? p.method ?? '—'}</td>
                <td className="cell-mute date">{date(p.paidAt)}</td>
                <td>
                  {p.reviewStatus === 'PENDING' ? (
                    <span className="badge badge-warning">بانتظار الاعتماد</span>
                  ) : (
                    <span className="badge badge-success">معتمدة</span>
                  )}
                </td>
                <td>
                  <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-sm btn-icon"
                      onClick={() => setReceiptFor(p)}
                      title="الإيصال المرفق"
                      aria-label={`إيصال التحصيل ${p.number}`}
                    >
                      <Icon name="eye" size={14} />
                    </button>
                    {canConfirm && p.reviewStatus === 'PENDING' && (
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={confirm.isPending}
                        onClick={() => confirm.mutate(p.id)}
                        title="اعتمد — المال وصل فعلًا"
                      >
                        اعتماد
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirm.isError && (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <div className="alert alert-danger">
            {confirm.error instanceof Error ? confirm.error.message : 'تعذّر الاعتماد.'}
          </div>
        </div>
      )}

      {/* الإيصال المرفق على الدفعة — يُراجَع قبل الاعتماد. */}
      {receiptFor && (
        <Modal title={`إيصال التحصيل ${receiptFor.number}`} onClose={() => setReceiptFor(null)}>
          <Attachments entity="payment" entityId={receiptFor.id} title="مرفقات الدفعة" />
        </Modal>
      )}
    </div>
  );
}
